import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Empty,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@store/store';
import { selectUserPermissions } from '@store/slices/userSlice.tsx';
import { ItemSelect } from '@tools/components/ItemSelect.tsx';
import ImageManager from '@tools/components/ImageManager.tsx';
import type { ImageInfo } from '@app-types/commons/images.types.ts';
// Importación actualizada: API movida a features/erp/subscriptions/api
import { subscriptionsApi } from '../api/subscriptions.ts';
import { ExportDataButton } from '@tools/export';
import type {
  CreatePlanPayload,
  SubscriptionPlan,
  SubscriptionPlanFilters,
  SubscriptionBillingPeriod,
} from '@app-types/subscriptions.ts';
import ResponsivePageHeading from '@components/layout/ResponsivePageHeading.tsx';
import {
  trackFormCompleted,
  trackFormValidationAttempt,
  type BehaviorAnalyticsContext,
} from '@tools/behaviorAnalytics.ts';

import './SubscriptionPlansPage.scss';

const { Title, Text } = Typography;

const BILLING_OPTIONS: { label: string; value: SubscriptionBillingPeriod }[] = [
  { label: 'Diario', value: 'daily' },
  { label: 'Semanal', value: 'weekly' },
  { label: 'Quincenal', value: 'biweekly' },
  { label: 'Mensual', value: 'monthly' },
  { label: 'Trimestral', value: 'quarterly' },
  { label: 'Semestral', value: 'biannual' },
  { label: 'Anual', value: 'annual' },
];

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  inactive: 'default',
};

const DEFAULT_PLAN_FORM: PlanFormValues = {
  name: '',
  description: '',
  billing_period: 'monthly',
  cycle_anchor_strategy: 'fixed_month_day',
  fixed_month_day: 10,
  fixed_weekday: null,
  payment_timing: 'prepaid',
  first_cycle_charge_policy: 'full',
  price: 0,
  is_active: true,
  plan_items: [],  // Empezar sin items, el usuario agrega los que necesita
};

type PlanFormValues = Omit<CreatePlanPayload, 'plan_items'> & {
  image?: ImageInfo | null;
  plan_items: Array<CreatePlanPayload['plan_items'][number] & { item?: { id: number; name: string; sku?: string | null } }>;
};

interface PlanFormModalProps {
  open: boolean;
  title: string;
  onCancel: () => void;
  onSubmit: (values: PlanFormValues) => void;
  submitting: boolean;
  initialValues?: PlanFormValues;
}

const PlanFormModal = ({ open, title, onCancel, onSubmit, submitting, initialValues }: PlanFormModalProps): JSX.Element => {
  const [form] = Form.useForm<PlanFormValues>();
  const [selectedImage, setSelectedImage] = useState<ImageInfo[]>([]);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const billingPeriod = Form.useWatch('billing_period', form) ?? 'monthly';
  const usesWeekday = billingPeriod === 'weekly' || billingPeriod === 'biweekly';
  const behaviorContext = useMemo<BehaviorAnalyticsContext>(() => ({
    appArea: 'erp',
    feature: 'subscriptions',
    surface: 'modal',
    entryPoint: 'subscription_plan_editor',
    componentId: 'subscription_plan_form',
  }), []);

  useEffect(() => {
    if (open) {
      const values = initialValues ?? DEFAULT_PLAN_FORM;
      // @ ts-expect-error - metadata type incompatibility between CreatePlanPayload and UpdatePlanPayload
      form.setFieldsValue(values);
      setSelectedImage(values.image ? [values.image] : []);
    }
  }, [form, initialValues, open]);

  const handleFinish = (values: PlanFormValues) => {
    const sanitizedItems = (values.plan_items ?? []).map((item) => ({
      ...item,
      quantity: item.is_unlimited ? undefined : item.quantity,
    }));
    trackFormCompleted(behaviorContext, {
      form_id: 'subscription_plan',
      items_count: sanitizedItems.length,
      mode: initialValues ? 'edit' : 'create',
    });
    const weekBased = values.billing_period === 'weekly' || values.billing_period === 'biweekly';
    onSubmit({
      ...values,
      cycle_anchor_strategy: weekBased ? 'fixed_weekday' : 'fixed_month_day',
      fixed_weekday: weekBased ? (values.fixed_weekday ?? 0) : null,
      fixed_month_day: weekBased ? null : (values.fixed_month_day ?? 10),
      first_cycle_charge_policy: 'full',
      image_id: selectedImage[0]?.id ?? null,
      plan_items: sanitizedItems,
    });
  };

  const handleFinishFailed = (errorInfo: { errorFields?: unknown[] }) => {
    trackFormValidationAttempt(behaviorContext, {
      form_id: 'subscription_plan',
      status: 'failed',
      mode: initialValues ? 'edit' : 'create',
      error_count: errorInfo.errorFields?.length ?? 0,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      title={title}
      confirmLoading={submitting}
      width={900}
      style={isMobile ? { top: 0, maxWidth: '100vw', paddingBottom: 0 } : undefined}
      okText="Guardar"
    >
      <Form<PlanFormValues>
        layout="vertical"
        form={form}
        initialValues={initialValues ?? DEFAULT_PLAN_FORM}
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Nombre"
              name="name"
              rules={[{ required: true, message: 'El nombre es requerido' }, { max: 255 }]}
            >
              <Input placeholder="Nombre del plan" maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Imagen del plan">
              <ImageManager
                value={selectedImage}
                onChange={(images) => setSelectedImage(images.slice(0, 1))}
                multiple={false}
                maxSelection={1}
                selectedSize="sm"
                triggerText="Elegir imagen"
                options={{ defaultFolderName: 'Suscripciones', defaultKind: 'images' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item
              label="Periodo de facturación"
              name="billing_period"
              rules={[{ required: true, message: 'Seleccioná un periodo' }]}
            >
              <Select options={BILLING_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item
              label="Precio"
              name="price"
              rules={[{ required: true, message: 'Ingresá el precio' }, { type: 'number', min: 0 }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} prefix="$" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            {usesWeekday ? (
              <Form.Item label="Día de vencimiento" name="fixed_weekday" initialValue={0}>
                <Select
                  options={['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((label, value) => ({ label, value }))}
                />
              </Form.Item>
            ) : (
              <Form.Item label="Día de vencimiento" name="fixed_month_day" initialValue={10}>
                <InputNumber min={1} max={28} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Col>
        </Row>

        <Collapse
          ghost
          items={[{
            key: 'advanced',
            label: 'Configuración avanzada',
            children: (
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Momento del pago" name="payment_timing">
                    <Segmented block options={[{ label: 'Pago al inicio', value: 'prepaid' }, { label: 'Pago al final', value: 'postpaid' }]} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Primera cuota">
                    <Input value="Cobro completo" readOnly />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Descripción" name="description">
                    <Input.TextArea rows={3} maxLength={500} placeholder="Detalles opcionales" />
                  </Form.Item>
                </Col>
              </Row>
            ),
          }]}
        />

        <Divider titlePlacement="start">Items del plan</Divider>
        <Form.List
          name="plan_items"
          rules={[
            {
              validator: async (_, value) => {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error('Agregá al menos un ítem'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Card
                  key={key}
                  size="small"
                  style={{ marginBottom: 12 }}
                  title={`Ítem ${name + 1}`}
                  extra={
                    fields.length > 1 ? (
                      <Button
                        danger
                        type="link"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        aria-label="Eliminar ítem"
                      >
                        Eliminar
                      </Button>
                    ) : null
                  }
                >
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.plan_items?.[name]?.item_id !== curr.plan_items?.[name]?.item_id}
                      >
                        {() => {
                          const planItem = form.getFieldValue(['plan_items', name]);
                          return (
                            <Form.Item
                              {...rest}
                              name={[name, 'item_id']}
                              label="Ítem (servicio o producto)"
                              rules={[{ required: true, message: 'Seleccioná un ítem' }]}
                              tooltip="Servicio o producto incluido en el plan"
                            >
                              <ItemSelect
                                placeholder="Buscar servicio o producto..."
                                style={{ width: '100%' }}
                                initialItem={planItem?.item}
                                allowedItemTypes={['product', 'service']}
                                behaviorContext={{
                                  appArea: 'erp',
                                  feature: 'subscriptions',
                                  surface: 'modal',
                                  entryPoint: 'subscription_plan_editor',
                                  componentId: 'item_search',
                                }}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={8}>
                      <Form.Item
                        {...rest}
                        name={[name, 'is_unlimited']}
                        label="Tipo de uso"
                        tooltip="Ilimitado = uso sin restricción. Limitado = cantidad fija por ciclo."
                      >
                        <Select
                          options={[
                            { value: true, label: '♾️ Ilimitado' },
                            { value: false, label: '🔢 Cantidad fija' },
                          ]}
                          onChange={() => {
                            // Limpiar cantidad si cambia a ilimitado
                            const isUnlimited = form.getFieldValue(['plan_items', name, 'is_unlimited']);
                            if (isUnlimited) {
                              form.setFieldValue(['plan_items', name, 'quantity'], undefined);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => {
                      const prevUnlimited = prev?.plan_items?.[name]?.is_unlimited;
                      const currUnlimited = curr?.plan_items?.[name]?.is_unlimited;
                      return prevUnlimited !== currUnlimited;
                    }}>
                      {() => {
                        const isUnlimited = form.getFieldValue(['plan_items', name, 'is_unlimited']);
                        if (isUnlimited !== false) return null;
                        return (
                          <Col xs={12} md={8}>
                            <Form.Item
                              {...rest}
                              name={[name, 'quantity']}
                              label="Cantidad por ciclo"
                              rules={[{ required: true, message: 'Ingresá la cantidad' }]}
                            >
                              <InputNumber min={1} style={{ width: '100%' }} placeholder="Ej: 4" />
                            </Form.Item>
                          </Col>
                        );
                      }}
                    </Form.Item>
                    <Col xs={12} md={6}>
                      <Form.Item {...rest} name={[name, 'usage_period']} label="Período de uso" initialValue="weekly">
                        <Select options={[{ value: 'weekly', label: 'Por semana' }, { value: 'monthly', label: 'Por mes' }]} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.ErrorList errors={errors} />
              <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ is_unlimited: false, usage_period: 'weekly' })}>
                Agregar ítem
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

const DEFAULT_FILTERS: SubscriptionPlanFilters = { page: 1, page_size: 10 };

const SubscriptionPlansPage = (): JSX.Element => {
  const [filters, setFilters] = useState<SubscriptionPlanFilters>(DEFAULT_FILTERS);
  const [isMobileFiltersExpanded, setIsMobileFiltersExpanded] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<SubscriptionPlan | null>(null);
  const [deletePlan, setDeletePlan] = useState<SubscriptionPlan | null>(null);

  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.is_active !== undefined) count++;
    if (filters.billing_period) count++;
    return count;
  }, [filters]);

  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  const canRead = permissionSet.has('subscriptions:plans:read') || permissionSet.has('subscriptions:read');
  const canWrite =
    permissionSet.has('subscriptions:plans:write') ||
    permissionSet.has('subscriptions:write') ||
    permissionSet.has('subscriptions:plans:admin');

  const plansQuery = useQuery({
    queryKey: ['subscription-plans', filters],
    queryFn: () => subscriptionsApi.getPlans(filters),
    enabled: canRead,
    placeholderData: (previousData) => previousData,
  });

  const createMutation = useMutation({
    mutationFn: subscriptionsApi.createPlan,
    onSuccess: () => {
      message.success('Plan creado');
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setCreateOpen(false);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { detail?: string })?.detail ?? 'No pudimos crear el plan';
      message.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ planId, payload }: { planId: number; payload: PlanFormValues }) =>
      subscriptionsApi.updatePlan(planId, payload),
    onSuccess: () => {
      message.success('Plan actualizado');
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setEditPlan(null);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { detail?: string })?.detail ?? 'No pudimos actualizar el plan';
      message.error(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ planId, force }: { planId: number; force?: boolean }) =>
      subscriptionsApi.deletePlan(planId, force),
    onSuccess: () => {
      message.success('Plan eliminado');
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setDeletePlan(null);
    },
    onError: () => message.error('No pudimos eliminar el plan'),
  });

  const handleFilterSubmit = (values: SubscriptionPlanFilters) => {
    setFilters((current) => ({ ...current, ...values, page: 1 }));
  };

  const handleTableChange = (page: number, pageSize?: number) => {
    setFilters((current) => ({ ...current, page, page_size: pageSize ?? current.page_size }));
  };

  const columns: ColumnsType<SubscriptionPlan> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (value: string, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{value}</Text>
          <Text type="secondary">{record.description || 'Sin descripción'}</Text>
        </Space>
      ),
    },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      render: (value: number | string) => `$${Number(value ?? 0).toFixed(2)}`,
      responsive: ['md'],
    },
    {
      title: 'Periodo',
      dataIndex: 'billing_period',
      key: 'billing_period',
      render: (value: SubscriptionBillingPeriod) =>
        BILLING_OPTIONS.find((option) => option.value === value)?.label ?? value,
    },
    {
      title: 'Items',
      dataIndex: 'plan_items',
      key: 'plan_items',
      render: (items: SubscriptionPlan['plan_items']) => items.length,
      align: 'center',
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? STATUS_COLOR.active : STATUS_COLOR.inactive}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      align: 'center',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => setEditPlan(record)}
            disabled={!canWrite}
          >
            Editar
          </Button>
          <Button
            type="link"
            icon={<DeleteOutlined />}
            danger
            onClick={() => setDeletePlan(record)}
            disabled={!canWrite}
          >
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  const plans = plansQuery.data?.items ?? [];
  const total = plansQuery.data?.total ?? 0;
  const pageSize = filters.page_size ?? 10;
  const currentPage = filters.page ?? 1;

  const renderMobilePlanCard = (plan: SubscriptionPlan) => {
    return (
      <Card
        key={plan.id}
        size="small"
        className="subscription-plans-page__mobile-card"
        onClick={() => setEditPlan(plan)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setEditPlan(plan);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Editar plan ${plan.name}`}
      >
        <div className="subscription-plans-page__mobile-card-head">
          <div className="subscription-plans-page__mobile-card-id">
            <Text strong ellipsis>{plan.name}</Text>
            <Text type="secondary">
              {BILLING_OPTIONS.find((option) => option.value === plan.billing_period)?.label} · {plan.plan_items.length} ítems
            </Text>
          </div>
          <div className="subscription-plans-page__mobile-card-value">
            <Text strong>${Number(plan.price).toFixed(2)}</Text>
          </div>
        </div>
        <div className="subscription-plans-page__mobile-card-main">
          <Tag color={plan.is_active ? STATUS_COLOR.active : STATUS_COLOR.inactive}>
            {plan.is_active ? 'Activo' : 'Inactivo'}
          </Tag>
          <Space size={4} onClick={(e) => e.stopPropagation()}>
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label={`Editar ${plan.name}`}
              onClick={() => setEditPlan(plan)}
              disabled={!canWrite}
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Eliminar ${plan.name}`}
              onClick={() => setDeletePlan(plan)}
              disabled={!canWrite}
            />
          </Space>
        </div>
      </Card>
    );
  };

  return (
    <div className="subscription-plans-page" style={{ width: '100%', padding: screens.md ? 24 : 12 }}>
      <div className="subscription-plans-page__container">
        <Breadcrumb
          items={[
            { title: 'Admin' },
            { title: 'Suscripciones' },
            { title: 'Planes' },
          ]}
        />

        <ResponsivePageHeading
          title="Planes de Suscripción"
          description={isMobile ? undefined : "Gestioná los planes y sus items"}
          actions={!isMobile ? (
            <Space>
              <ExportDataButton
                urlName="erp:subscriptions:plans_export"
                filters={filters}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
                disabled={!canWrite}
              >
                Nuevo Plan
              </Button>
            </Space>
          ) : null}
        />
        <Title level={3} style={{ marginTop: 16 }} hidden>Planes de suscripción</Title>

        {/* Filters */}
        {isMobile ? (
          <Space orientation="vertical" size={8} style={{ width: '100%', marginBottom: 16 }}>
            <div className="subscription-plans-page__mobile-filter-row">
              <Input
                placeholder="Buscar..."
                value={filters.search ?? ''}
                onChange={(e) => handleFilterSubmit({ ...filters, search: e.target.value || undefined })}
                style={{ flex: 1 }}
                allowClear
              />
              <Button
                type={isMobileFiltersExpanded || activeFilterCount > 0 ? 'primary' : 'default'}
                ghost={isMobileFiltersExpanded || activeFilterCount > 0}
                icon={<FilterOutlined />}
                onClick={() => setIsMobileFiltersExpanded((prev) => !prev)}
              >
                Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
              <ExportDataButton
                urlName="erp:subscriptions:plans_export"
                filters={filters}
              />
            </div>
            {isMobileFiltersExpanded && (
              <div className="subscription-plans-page__mobile-filters-panel">
                <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                  <Select
                    placeholder="Estado"
                    allowClear
                    options={[
                      { label: 'Activos', value: true },
                      { label: 'Inactivos', value: false },
                    ]}
                    value={filters.is_active}
                    onChange={(val) => handleFilterSubmit({ ...filters, is_active: val })}
                    style={{ width: '100%' }}
                  />
                  <Select
                    placeholder="Periodo"
                    allowClear
                    options={BILLING_OPTIONS}
                    value={filters.billing_period}
                    onChange={(val) => handleFilterSubmit({ ...filters, billing_period: val })}
                    style={{ width: '100%' }}
                  />
                  <Button block type="primary" onClick={() => plansQuery.refetch()}>Recargar</Button>
                  {activeFilterCount > 0 && (
                    <Button
                      type="link"
                      danger
                      block
                      onClick={() => {
                        setFilters(DEFAULT_FILTERS);
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </Space>
              </div>
            )}
            {canWrite && (
              <Button
                type="primary"
                block
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
              >
                Nuevo Plan
              </Button>
            )}
          </Space>
        ) : (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Form<SubscriptionPlanFilters>
              layout="vertical"
              initialValues={filters}
              onFinish={handleFilterSubmit}
            >
              <Row gutter={[12, 12]}>
                <Col xs={24} md={10}>
                  <Form.Item label="Buscar" name="search">
                    <Input allowClear placeholder="Nombre o descripción" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Estado" name="is_active">
                    <Select
                      allowClear
                      options={[
                        { label: 'Activos', value: true },
                        { label: 'Inactivos', value: false },
                      ]}
                      placeholder="Todos"
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label="Periodo" name="billing_period">
                    <Select allowClear options={BILLING_OPTIONS} placeholder="Todos" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button type="primary" htmlType="submit" block>
                    Filtrar
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        )}

        {/* Main Content */}
        {plans.length === 0 ? (
          <Card>
            <Empty description="Sin datos por el momento" />
          </Card>
        ) : isMobile ? (
          <Space orientation="vertical" size={8} className="subscription-plans-page__mobile-list">
            {plansQuery.isFetching && plans.length === 0 ? (
              <Card size="small" className="subscription-plans-page__mobile-card">
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
              </Card>
            ) : (
              plans.map(renderMobilePlanCard)
            )}
            <div className="subscription-plans-page__mobile-pagination">
              <Pagination
                size="small"
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={handleTableChange}
                showSizeChanger={false}
                showLessItems
                responsive
              />
              <Typography.Text type="secondary" className="subscription-plans-page__mobile-total">
                {total} registros
              </Typography.Text>
            </div>
          </Space>
        ) : (
          <Card bodyStyle={{ padding: 24 }}>
            <Table<SubscriptionPlan>
              rowKey={(plan) => plan.id}
              columns={columns}
              dataSource={plans}
              loading={plansQuery.isFetching}
              pagination={{
                total,
                current: currentPage,
                pageSize,
                onChange: handleTableChange,
                showSizeChanger: true,
                responsive: true,
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        )}
      </div>

      <PlanFormModal
        open={createOpen}
        title="Crear plan"
        onCancel={() => setCreateOpen(false)}
        submitting={createMutation.isPending}
        onSubmit={(values) => createMutation.mutate(values)}
      />

      {editPlan ? (
        <PlanFormModal
          open={Boolean(editPlan)}
          title={`Editar ${editPlan.name}`}
          onCancel={() => setEditPlan(null)}
          submitting={updateMutation.isPending}
          initialValues={{
            name: editPlan.name,
            description: editPlan.description ?? undefined,
            billing_period: editPlan.billing_period,
            cycle_anchor_strategy: editPlan.cycle_anchor_strategy === 'fixed_weekday' ? 'fixed_weekday' : 'fixed_month_day',
            fixed_weekday: editPlan.fixed_weekday ?? null,
            fixed_month_day: editPlan.fixed_month_day ?? null,
            payment_timing: editPlan.payment_timing,
            first_cycle_charge_policy: 'full',
            billing_item_id: editPlan.billing_item_id ?? 0,
            price: Number(editPlan.price),  // Convertir a número por si viene como string
            is_active: editPlan.is_active,
            metadata: editPlan.metadata ?? undefined,
            plan_items: editPlan.plan_items,
          }}
          onSubmit={(values) => updateMutation.mutate({ planId: editPlan.id, payload: values })}
        />
      ) : null}

      {deletePlan ? (
        <Modal
          open={Boolean(deletePlan)}
          title={`Eliminar ${deletePlan.name}`}
          onCancel={() => setDeletePlan(null)}
          onOk={() => deleteMutation.mutate({ planId: deletePlan.id })}
          confirmLoading={deleteMutation.isPending}
          okButtonProps={{ danger: true }}
        >
          <Space orientation="vertical" size="small">
            <Text>Esta acción desactivará el plan.</Text>
            <Text type="warning">Si tiene suscripciones activas no se eliminará físicamente.</Text>
          </Space>
        </Modal>
      ) : null}
    </div>
  );
};

export default SubscriptionPlansPage;
